import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button, Form, Table, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { useAppContext } from '../context/AppContext';

const getAuthToken = () => localStorage.getItem('authToken');

// ✅ Define allowed fields here
const allowedFields = [
  'Ad. No',
  'Name',
  'Gender',
  'DOB',
  'Parents',
  'Mobile',
  'Class',
  'Division',
  'Address',
  'Medium',
  'School Name',
  'School Reg. No',
];
const fieldKeyMap = {
  'Ad. No': 'admission_no',
  Name: 'name',
  Gender: 'gender',
  DOB: 'dob',
  Parents: 'parents',
  Mobile: 'mobile',
  Class: 'class',
  Division: 'division',
  Address: 'address',
  Medium: 'medium',
  'School Name': 'school_name',
  'School Reg. No': 'school_reg_no',
};

const MONTH_NAME_MAP = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const normalizeYear = (yearNum) => {
  if (yearNum >= 1000 && yearNum <= 9999) {
    return yearNum;
  }
  if (yearNum >= 0 && yearNum <= 99) {
    // 2-digit year: 00-50 -> 2000-2050, 51-99 -> 1951-1999
    return yearNum <= 50 ? 2000 + yearNum : 1900 + yearNum;
  }
  return yearNum;
};

const parseDateComponents = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  // 1. If it is already a JS Date object
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      day: value.getDate(),
      month: value.getMonth() + 1,
      year: value.getFullYear(),
    };
  }

  // 2. If it is a numeric Excel date serial code
  if (typeof value === 'number') {
    if (value >= 1 && value <= 100000) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed?.y && parsed?.m && parsed?.d) {
        return {
          day: parsed.d,
          month: parsed.m,
          year: parsed.y,
        };
      }
    }
  }

  let str = String(value).trim();
  // Strip leading/trailing single/double quotes and apostrophes
  str = str.replace(/^['"]+|['"]+$/g, '').trim();

  if (!str || str === '0000-00-00' || str.startsWith('0000')) {
    return null;
  }

  // 3. 8-digit numeric string (YYYYMMDD or DDMMYYYY)
  if (/^\d{8}$/.test(str)) {
    const first4 = parseInt(str.slice(0, 4), 10);
    const last4 = parseInt(str.slice(4), 10);
    if (first4 >= 1900 && first4 <= 2100) {
      // YYYYMMDD
      const y = first4;
      const m = parseInt(str.slice(4, 6), 10);
      const d = parseInt(str.slice(6, 8), 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return { day: d, month: m, year: y };
      }
    } else if (last4 >= 1900 && last4 <= 2100) {
      // DDMMYYYY
      const d = parseInt(str.slice(0, 2), 10);
      const m = parseInt(str.slice(2, 4), 10);
      const y = last4;
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return { day: d, month: m, year: y };
      }
    }
  }

  // 4. Split by any common date separators (dot, hyphen, slash, backslash, vertical bar, space, unicode dashes)
  const parts = str.split(/[\s.\-–—−/\\|,]+/).filter(Boolean);

  if (parts.length === 3) {
    const [p1, p2, p3] = parts;

    // Case A: ISO format YYYY-MM-DD or YYYY-MMM-DD (Year first)
    if (/^\d{4}$/.test(p1)) {
      const year = parseInt(p1, 10);
      let month = parseInt(p2, 10);
      if (Number.isNaN(month)) {
        month = MONTH_NAME_MAP[p2.toLowerCase()] || 0;
      }
      const day = parseInt(p3, 10);
      if (
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31 &&
        year >= 1900 &&
        year <= 2100
      ) {
        return { day, month, year };
      }
    }

    // Case B: DD-MM-YYYY / DD-MM-YY / DD-MMM-YYYY / DD-MMM-YY (Day first - per requirement)
    let day = parseInt(p1, 10);
    let month = parseInt(p2, 10);
    if (Number.isNaN(month)) {
      month = MONTH_NAME_MAP[p2.toLowerCase()] || 0;
    }
    let rawYear = parseInt(p3, 10);

    if (
      !Number.isNaN(day) &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12 &&
      !Number.isNaN(rawYear)
    ) {
      const year = normalizeYear(rawYear);
      if (year >= 1900 && year <= 2100) {
        return { day, month, year };
      }
    }
  }

  return null;
};

const normalizeDobValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const parsed = parseDateComponents(value);
  if (parsed) {
    const day = String(parsed.day).padStart(2, '0');
    const month = String(parsed.month).padStart(2, '0');
    return `${day}-${month}-${parsed.year}`;
  }
  return String(value).trim();
};

const convertDobToBackendFormat = (value) => {
  if (!value) {
    return '';
  }
  const parsed = parseDateComponents(value);
  if (parsed) {
    const day = String(parsed.day).padStart(2, '0');
    const month = String(parsed.month).padStart(2, '0');
    return `${parsed.year}-${month}-${day}`;
  }
  return String(value).trim();
};

const findFieldValue = (row, targetField) => {
  if (row[targetField] !== undefined && row[targetField] !== null) {
    return row[targetField];
  }
  const cleanTarget = targetField.trim().toLowerCase();
  for (const key of Object.keys(row)) {
    if (key.trim().toLowerCase() === cleanTarget) {
      return row[key];
    }
  }
  if (targetField === 'DOB') {
    for (const key of Object.keys(row)) {
      const k = key.trim().toLowerCase();
      if (
        k === 'date of birth' ||
        k === 'date_of_birth' ||
        k === 'birth date' ||
        k === 'birthdate'
      ) {
        return row[key];
      }
    }
  }
  return undefined;
};

const BulkUploadStudents = () => {
  const [parsedData, setParsedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { selectedAcademicYear } = useAppContext();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true });

      // Filter out only allowed fields
      const cleanedData = jsonData.map((row) => {
        const filtered = {};
        allowedFields.forEach((field) => {
          const val = findFieldValue(row, field);
          if (val !== undefined && val !== null) {
            filtered[field] =
              field === 'DOB' ? normalizeDobValue(val) : val;
          }
        });
        return filtered;
      });

      setParsedData(cleanedData);
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {}, [parsedData]);

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      alert('No valid data to submit.');
      return;
    }

    if (!selectedAcademicYear?.id) {
      alert('Please select an academic year.');
      return;
    }

    setLoading(true);
    try {
      const token = getAuthToken();

      // Convert keys to backend field names before sending
      /*const transformedData = parsedData.map((row) => {
      const transformedRow = {};
      Object.keys(row).forEach((userField) => {
        const backendField = fieldKeyMap[userField];
        if (backendField) {
          transformedRow[backendField] = row[userField];
        }
      });
      return transformedRow;
    });*/

      const transformedData = parsedData.map((row) => {
        const transformedRow = {};
        Object.keys(row).forEach((userField) => {
          const backendField = fieldKeyMap[userField];
          if (
            backendField &&
            backendField !== 'class' &&
            backendField !== 'division'
          ) {
            transformedRow[backendField] =
              backendField === 'dob'
                ? convertDobToBackendFormat(row[userField])
                : row[userField];
          }
        });

        // Handle multiple class/division combinations
        const classList = (row['Class'] || '')
          .toString()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const divisionList = (row['Division'] || '')
          .toString()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        transformedRow.class_divisions = classList.map((cls, i) => ({
          class: cls,
          division: divisionList[i] || divisionList[0] || '',
        }));

        return transformedRow;
      });

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}bulk-upload-students-v2?academic_year_id=${selectedAcademicYear.id}`,
        { students: transformedData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success') {
        // Check if partial errors exist in results
        const results = response.data.results || [];
        const errors = results.filter((r) => r.status === 'error');
        const successCount = results.length - errors.length;

        if (errors.length > 0) {
          alert(
            `Upload completed with some errors.\n` +
              `${successCount} students processed successfully.\n` +
              `Errors:\n` +
              errors
                .map((e) => `AdNo: ${e.admission_no || 'N/A'} - ${e.message}`)
                .join('\n'),
          );
        } else {
          alert('Upload successful! All students processed.');
        }

        setParsedData([]); // clear only on success or partial success
      } else {
        alert(response.data.message || 'Upload failed.');
      }
    } catch (err) {
      console.error('Upload error:', err);

      // More detailed message if available
      const message =
        err.response?.data?.message ||
        err.message ||
        'Failed to upload data. Please try again.';

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <h4>Bulk Upload Students</h4>
      <Form.Group className="mb-3">
        <Form.Label>Select Excel File</Form.Label>
        <Form.Control
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
        />
      </Form.Group>

      {parsedData.length > 0 && (
        <>
          <h5>Preview</h5>
          <div className="table-responsive mb-3">
            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  {allowedFields.map((field) => (
                    <th key={field}>{field}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedData.map((row, index) => (
                  <tr key={index}>
                    {allowedFields.map((field) => (
                      <td key={field}>{row[field] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Button variant="success" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Submit All'}
          </Button>
        </>
      )}
    </Layout>
  );
};

export default BulkUploadStudents;
